import { ToastContext } from "context/ToastContext";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { useContext, useMemo, useState } from "react";
import {
  useCreatePayerOverrideMutation,
  useDeletePayerOverrideMutation,
  useGetPayerOverridesQuery,
  useUpdatePayerOverrideMutation,
} from "store/queries/benefitPayerProfile";
import styled from "styled-components";
import {
  IPayerOverrideRow,
  IPayerOverrideSaveRequest,
} from "types/BenefitSelection/BenefitSelection";

import { PayerOverrideDialog } from "./PayerOverrideDialog";
import {
  benefitLabel,
  datesSentence,
  EVERY_ESTIMATE,
  formatOverrideValue,
  overrideSentence,
  scopeParts,
} from "./payerOverrideWording";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
`;

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

/**
 * CLE-2792 — PAYER OVERRIDES: what a payer's plans really charge, where the response is wrong or
 * silent.
 *
 * These eleven rows had no editor at all. Adding a twelfth was a code change and a deploy, which is
 * why three of the four live rules were still in `eligibility.service.ts` as literals a year after
 * anybody could say why.
 *
 * 🚨 EVERY ROW IS A MONEY DECISION FOR EVERY ACCOUNT IN ITS SCOPE, which is why the table leads with
 * the sentence rather than the fields, and why the delete confirm reads the row back as that
 * sentence plus its scope. "REPLACING", "CORRECTING" and "after source" appear nowhere on this
 * screen — see `payerOverrideWording.ts` for what each shape is called and why.
 *
 * ⚠️ NEWEST FIRST, not grouped by payer. The question this list answers is "what has been changed
 * lately"; a payer grouping would answer "what does Aetna do", which is the payer page's question
 * and needs a payer column the API does not index on.
 */
export function PayerOverridesTab() {
  // `ToastContext` defaults to null, so read it defensively rather than destructuring it.
  const toast = useContext(ToastContext);
  const { data, isLoading, isError } = useGetPayerOverridesQuery();

  const [createRow, { isLoading: creating }] = useCreatePayerOverrideMutation();
  const [updateRow, { isLoading: updating }] = useUpdatePayerOverrideMutation();
  const [deleteRow] = useDeletePayerOverrideMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<IPayerOverrideRow | null>(null);
  const [filter, setFilter] = useState("");

  const rows = data?.data ?? [];

  /** Payer and plan text only — the two things somebody arrives knowing. */
  const visibleRows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle.length === 0) return rows;
    return rows.filter((row) =>
      [row.payerName, row.requestedPayerName, row.planName, row.policyType]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(needle))
    );
  }, [rows, filter]);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleSave = async (body: IPayerOverrideSaveRequest) => {
    try {
      if (editing) {
        await updateRow({ id: editing.id, body }).unwrap();
      } else {
        await createRow(body).unwrap();
      }
      setDialogOpen(false);
      toast?.current?.show({
        severity: "success",
        summary: "Payer override saved",
        detail: "New eligibility checks use it.",
      });
    } catch (error: any) {
      toast?.current?.show({
        severity: "error",
        summary: "Not saved",
        detail:
          error?.data?.message ||
          error?.message ||
          "The payer override could not be saved.",
      });
    }
  };

  // 🚨 THE CONFIRM READS THE ROW BACK, it does not ask "are you sure". A row is a sentence about a
  // payer and a scope; "Delete this override?" is a question nobody can answer without scrolling
  // back to the table they just clicked in.
  const confirmRemove = (row: IPayerOverrideRow) =>
    confirmDialog({
      header: "Remove this payer override?",
      style: { width: "min(560px, 95vw)" },
      message: `${overrideSentence(row)} — ${benefitLabel(
        row.benefit
      )}, ${formatOverrideValue(row.value, row.benefit)}. Applies to: ${
        scopeParts(row).join(" · ") || EVERY_ESTIMATE
      }. Removing it means new checks read whatever the payer reports instead.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Remove",
      rejectLabel: "Keep it",
      accept: async () => {
        try {
          await deleteRow(row.id).unwrap();
        } catch (error: any) {
          toast?.current?.show({
            severity: "error",
            summary: "Not removed",
            detail: error?.data?.message || "The row could not be removed.",
          });
        }
      },
    });

  const kindCellBody = (row: IPayerOverrideRow) => (
    <span
      className="font-medium text-sm"
      data-testid={`payerOverrides.${row.id}.kind`}
    >
      {overrideSentence(row)}
    </span>
  );

  const benefitCellBody = (row: IPayerOverrideRow) => (
    <span className="text-sm">{benefitLabel(row.benefit)}</span>
  );

  const valueCellBody = (row: IPayerOverrideRow) => (
    <span
      className="text-sm font-medium"
      data-testid={`payerOverrides.${row.id}.value`}
    >
      {formatOverrideValue(row.value, row.benefit)}
    </span>
  );

  const scopeCellBody = (row: IPayerOverrideRow) => {
    const parts = scopeParts(row);
    return (
      <div
        className="flex flex-column gap-1"
        data-testid={`payerOverrides.${row.id}.scope`}
      >
        {parts.length === 0 ? (
          <span className="text-sm">{EVERY_ESTIMATE}</span>
        ) : (
          parts.map((part) => (
            <span key={part} className="text-sm">
              {part}
            </span>
          ))
        )}
      </div>
    );
  };

  const datesCellBody = (row: IPayerOverrideRow) => (
    <span className="text-sm" data-testid={`payerOverrides.${row.id}.dates`}>
      {datesSentence(row) ?? <span className="text-color-secondary">—</span>}
    </span>
  );

  const reasonCellBody = (row: IPayerOverrideRow) => (
    <span className="text-sm text-color-secondary">{row.reason}</span>
  );

  const actionsCellBody = (row: IPayerOverrideRow) => (
    <div className="flex gap-1">
      <Button
        type="button"
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        aria-label={`Edit ${overrideSentence(row)}`}
        data-testid={`payerOverrides.${row.id}.edit`}
        onClick={() => {
          setEditing(row);
          setDialogOpen(true);
        }}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        aria-label={`Remove ${overrideSentence(row)}`}
        data-testid={`payerOverrides.${row.id}.remove`}
        onClick={() => confirmRemove(row)}
      />
    </div>
  );

  return (
    <Surface data-testid="payerOverridesTab">
      <div className="flex align-items-start justify-content-between gap-3 mb-3">
        <span className="text-sm text-color-secondary">
          A payer override says what a payer&apos;s plans really charge, where
          the response is wrong or reports nothing. It applies to every account
          in its scope.
        </span>
        <Button
          type="button"
          icon="pi pi-plus"
          label="Add override"
          className="p-button-outlined p-button-sm"
          data-testid="payerOverrides.add"
          onClick={() => openAdd()}
        />
      </div>

      <div className="mb-3">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            className="w-20rem"
            placeholder="Filter by payer or plan"
            aria-label="Filter by payer or plan"
            data-testid="payerOverrides.filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </span>
      </div>

      {isError && (
        <StyledMessage
          severity="error"
          text="The payer overrides could not be loaded."
        />
      )}

      <StyledDataTable
        value={visibleRows}
        dataKey="id"
        loading={isLoading}
        emptyMessage="No payer overrides. Every benefit is read from the response as the account's configuration selects it."
        data-testid="payerOverrides.table"
        className="mb-4"
      >
        <Column header="Override" body={kindCellBody} />
        <Column header="Benefit" body={benefitCellBody} />
        <Column header="Amount" body={valueCellBody} />
        <Column header="Applies to" body={scopeCellBody} />
        <Column header="Dates" body={datesCellBody} />
        <Column header="Why" body={reasonCellBody} />
        <Column header="" style={{ width: "7rem" }} body={actionsCellBody} />
      </StyledDataTable>

      <PayerOverrideDialog
        visible={dialogOpen}
        row={editing}
        saving={creating || updating}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />
    </Surface>
  );
}
